import i18nFactory from '@yandex-int/i18n';
import { Action } from 'redux';
import { ofType } from 'redux-observable';
import { EMPTY, from, Observable } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';

import {
    acceptMinusPhraseGroupsMassActions,
    OPEN_MINUS_PHRASE_GROUPS_MASS_ACTIONS
} from '../../actions/groups-edit/groups-edit-mass-actions';
import {
    ACCEPT_LIBRARY_LIST_EDITOR_DATA,
    AcceptLibraryListEditorDataAction,
    closeModalAddLibraryList,
    FETCH_LIBRARY_LISTS_FOR_EDIT,
    fetchLibraryListsForEdit,
    FetchLibraryListsForEditAction,
    getLibraryLists,
    OPEN_MINUS_PHRASES_EDITOR,
    OpenMinusPhrasesEditor,
    setAddedLibraryList,
    showErrorLibraryListEditor,
    showGetLibraryListsError,
    updateMinusPhraseEditorByMassAction
} from '../../actions/minusPhrasesEditor';
import { FetchMinusPhraseLibraryDataResult } from '../../api/MinusPhraseLibraryProvider/MinusPhraseLibraryProvider';
import { MinusPhraseMutationResultData } from '../../api/MinusPhraseLibraryProvider/MinusPhraseMutationExecutor';
import { BulkReplacementRadio } from '../../components/BulkReplacementModeChooser/BulkReplacementModeChooser';
import {
    formatMinusPhrasesToApi,
    mapValidationResultItemToErrorData,
    validateMinusPhraseData
} from '../../mappers/minus-phrase/minus-phrase';
import { mapFetchResultToLibraryItem } from '../../mappers/minusPhrases';
import { selectMinusPhrasesLibraryConstants } from '../../selectors/minusPhraseLibrary/minusPhraseLibrary';
import { selectGroupLibraryListEditorInfo } from '../../selectors/minusPhrases/minusPhrases';
import { ApiMethodExecutingError } from '../../service/Errors';
import { showNoticeMessage } from '../../service/GridHelpers/GridHelpers';
import { DnaEpic, DnaEpicAction } from '../../typings/epic';
import { addMinusKeywordsPacks } from '../../typings/graphql-grid';
import { NoticeType } from '../../typings/grid';
import { MassActionId } from '../../typings/groupsEditState';

import * as keyset from './minusPhrasesEditor.i18n';

const i18n = i18nFactory(keyset);

export const openEditorMinusPhraseLibraryEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(OPEN_MINUS_PHRASES_EDITOR, OPEN_MINUS_PHRASE_GROUPS_MASS_ACTIONS),
        filter(() => {
            return Boolean(!store$.value.minusPhraseLibraryEditor.isLibraryListsFetched);
        }),
        map((action: OpenMinusPhrasesEditor) => {
            return fetchLibraryListsForEdit(action.editorId);
        })
    );
};

export const fetchLibraryListsEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_LIBRARY_LISTS_FOR_EDIT),
        mergeMap(
            (action: FetchLibraryListsForEditAction): Observable<Action> => {
                const offset = 0;
                const constants = store$.value.commonData.constants;
                const { groups } = store$.value.editGroups;
                const groupsMassActionsData = store$.value.groupsEditMassActions;
                const { data, libraryItems } = store$.value.minusPhraseLibraryEditor;

                if (!constants) {
                    return EMPTY;
                }

                const limit = constants.minusPhraseLibraryConstants.maxLibraryPacksCount;

                return from(deps.minusPhraseLibraryDataProvider.fetchMinusPhraseLibraryData(offset, limit)).pipe(
                    mergeMap(
                        (response: FetchMinusPhraseLibraryDataResult): Array<Action> => {
                            const result: Array<Action> = [
                                getLibraryLists(action.editorId, mapFetchResultToLibraryItem(response.data))
                            ];

                            if (action.addedLibraryList) {
                                const maxLinkedPacksToOneAdGroup =
                                    constants.minusPhraseLibraryConstants.maxLinkedPacksToOneAdGroup;

                                return [
                                    ...result,
                                    setAddedLibraryList(
                                        action.editorId,
                                        action.addedLibraryList,
                                        maxLinkedPacksToOneAdGroup
                                    )
                                ];
                            }

                            if (store$.value.minusPhraseLibraryEditor.libraryItemsIsUpdating) {
                                if (action.editorId === MassActionId.groupsEdit) {
                                    const {
                                        minusKeywordsEditor: { value, selectedLibraryListItems }
                                    } = data[action.editorId];
                                    const isDelete =
                                        groupsMassActionsData[action.editorId].data.minusPhrasesRadio ===
                                        BulkReplacementRadio.DELETE;

                                    result.push(
                                        updateMinusPhraseEditorByMassAction(
                                            Object.keys(groups),
                                            value,
                                            selectedLibraryListItems,
                                            libraryItems,
                                            isDelete
                                        ),
                                        acceptMinusPhraseGroupsMassActions(action.editorId)
                                    );
                                }

                                return result;
                            }

                            return result;
                        }
                    ),
                    catchError(() => {
                        return [showGetLibraryListsError(action.editorId)];
                    })
                );
            }
        )
    );
};

export const addLibraryListEditorEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(ACCEPT_LIBRARY_LIST_EDITOR_DATA),
        mergeMap(
            (action: AcceptLibraryListEditorDataAction): DnaEpicAction => {
                const state = store$.value;

                const constants = selectMinusPhrasesLibraryConstants(state);
                const data = selectGroupLibraryListEditorInfo(state, action.editorId);

                if (!constants || !data) {
                    return EMPTY;
                }

                const listTitle = data.listTitle;
                const minusPhrases = data.minusPhrases;

                const clientValidationResult = validateMinusPhraseData(
                    {
                        title: listTitle,
                        minusPhrases
                    },
                    'addItems',
                    constants
                );

                if (clientValidationResult.length) {
                    return Observable.of(
                        showErrorLibraryListEditor(
                            action.editorId,
                            mapValidationResultItemToErrorData(clientValidationResult)
                        )
                    );
                }

                return Observable.fromPromise(
                    deps.minusPhraseLibraryMutationExecutor.addMinusKeywordsPack({
                        addItems: [
                            {
                                name: listTitle,
                                minusKeywords: formatMinusPhrasesToApi(minusPhrases)
                            }
                        ]
                    })
                ).pipe(
                    mergeMap(
                        (response: MinusPhraseMutationResultData): DnaEpicAction => {
                            const { validationResult } = response;

                            if (validationResult) {
                                return [
                                    showErrorLibraryListEditor(
                                        action.editorId,
                                        mapValidationResultItemToErrorData(validationResult)
                                    )
                                ];
                            }

                            showNoticeMessage(NoticeType.success, i18n('add-minus-keywords-pack-success'));

                            const addedItems = (response.data as addMinusKeywordsPacks).addMinusKeywordsPacks
                                .addedItems;
                            const addedItem = addedItems[0];

                            if (!addedItem) {
                                return EMPTY;
                            }

                            const addedListId = String(addedItem.id);

                            return [
                                closeModalAddLibraryList(action.editorId),
                                fetchLibraryListsForEdit(action.editorId, addedListId)
                            ];
                        }
                    ),
                    catchError((err: ApiMethodExecutingError) => {
                        return Observable.of(
                            showErrorLibraryListEditor(
                                action.editorId,
                                mapValidationResultItemToErrorData(err.getErrors())
                            )
                        );
                    })
                );
            }
        )
    );
};
